import React from 'react';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CampaignProgressBar } from './CampaignProgressBar';
import { CampaignMetricItem } from './CampaignMetricItem';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import type { CampaignResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';

interface CampaignCardProps {
  campaign: CampaignResponse;
  channelName?: string;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onActivate?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
}

/**
 * Campaign Card (view "Cards"). Padrão-lista DS (F34: cards SEM checkbox —
 * seleção em massa só na view de lista/tabela): card plano, botão inline de
 * status (play/pause) + menu ••• status-aware, métricas 2x2, barra de progresso
 * e rodapé canal/data.
 */
export const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  channelName,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onActivate,
  onPause,
  onResume,
  onCancel,
}) => {
  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Normalização POR CAMPO — `campaign.stats` pode existir parcial (campanha
  // antiga/draft) e `stats.messagesSent.toLocaleString()` explodia a tela inteira
  const stats = {
    messagesSent: campaign.stats?.messagesSent ?? 0,
    messagesDelivered: campaign.stats?.messagesDelivered ?? 0,
    messagesRead: campaign.stats?.messagesRead ?? 0,
    messagesFailed: campaign.stats?.messagesFailed ?? 0,
    totalRecipients: campaign.stats?.totalRecipients ?? 0,
  };

  const totalRecipients = stats.totalRecipients || campaign.recipientIds?.length || 0;
  const { status } = campaign;

  // Ação rápida inline (mockup): play para draft/paused, pause para agendada/em progresso.
  // `.kbtn` do mockup: fundo colorido tonalizado (retomar/ativar verde #dcfce7, pausar âmbar #fef3c7)
  const quickAction: { icon: 'play' | 'pause'; title: string; color: string; bgColor: string; onClick: () => void } | null =
    status === 'draft' && onActivate
      ? { icon: 'play', title: 'Ativar campanha', color: 'var(--theme-success-color)', bgColor: '#dcfce7', onClick: () => onActivate(campaign.id) }
      : (status === 'scheduled' || status === 'in_progress') && onPause
        ? { icon: 'pause', title: 'Pausar campanha', color: 'var(--theme-warning-color)', bgColor: '#fef3c7', onClick: () => onPause(campaign.id) }
        : status === 'paused' && onResume
          ? { icon: 'play', title: 'Retomar campanha', color: 'var(--theme-success-color)', bgColor: '#dcfce7', onClick: () => onResume(campaign.id) }
          : null;

  const actions: CardAction[] = [];
  if (status === 'draft' && onActivate) actions.push({ label: 'Ativar', icon: 'play', onClick: () => onActivate(campaign.id) });
  if ((status === 'scheduled' || status === 'in_progress') && onPause) actions.push({ label: 'Pausar', icon: 'pause', onClick: () => onPause(campaign.id) });
  if (status === 'paused' && onResume) actions.push({ label: 'Retomar', icon: 'play', onClick: () => onResume(campaign.id) });
  if ((status === 'scheduled' || status === 'in_progress' || status === 'paused') && onCancel) actions.push({ label: 'Cancelar', icon: 'square', onClick: () => onCancel(campaign.id) });
  if (onView) actions.push({ label: 'Ver relatório', icon: 'eye', onClick: () => onView(campaign.id) });
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(campaign.id) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(campaign.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(campaign.id), variant: 'danger' });

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-pointer"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      onClick={() => onView?.(campaign.id)}
      data-testid={`campaign-card-${campaign.id}`}
    >
      {/* Header: nome + ação rápida + menu */}
      <div className="flex items-start gap-2">
        <h3 className="flex-1 min-w-0 font-bold text-[15px] truncate" style={{ color: 'var(--theme-card-text)' }}>
          {campaign.name}
        </h3>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={stop}>
          {quickAction && (
            <button
              onClick={(e) => { stop(e); quickAction.onClick(); }}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: quickAction.color, backgroundColor: quickAction.bgColor }}
              title={quickAction.title}
              data-testid={`campaign-quick-${campaign.id}`}
            >
              <Icon name={quickAction.icon} size={16} />
            </button>
          )}
          {actions.length > 0 && <CardActionsMenu actions={actions} size="sm" data-testid={`campaign-card-actions-${campaign.id}`} />}
        </div>
      </div>

      {/* Status */}
      <div className="mt-2.5">
        <CampaignStatusBadge status={campaign.status} autoPause={campaign.autoPause} data-testid={`campaign-status-${campaign.id}`} />
      </div>

      {/* Métricas */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <CampaignMetricItem icon={'send'} label="Enviados" value={stats.messagesSent} total={totalRecipients} />
        <CampaignMetricItem icon={'check-circle'} label="Entregues" value={stats.messagesDelivered} total={stats.messagesSent} color="var(--theme-success-color)" />
        <CampaignMetricItem icon={'eye'} label="Lidos" value={stats.messagesRead} total={stats.messagesDelivered} color="var(--theme-primary)" />
        <CampaignMetricItem icon={'x-circle'} label="Falhas" value={stats.messagesFailed} total={stats.messagesSent} isError={stats.messagesFailed > 0} />
      </div>

      {/* Progresso — mockup: barra + "X / Y · Z%" SEMPRE visíveis (cheia quando
          concluída, vazia quando agendada/rascunho) */}
      <div className="mt-3">
        <CampaignProgressBar
          current={stats.messagesSent}
          total={totalRecipients}
          animated={campaign.status === 'in_progress'}
          showLabels={false}
        />
        <div className="mt-1.5 text-xs" style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}>
          {stats.messagesSent.toLocaleString('pt-BR')} / {totalRecipients.toLocaleString('pt-BR')} · {totalRecipients > 0 ? Math.round((stats.messagesSent / totalRecipients) * 100) : 0}%
        </div>
      </div>

      {/* Rodapé: canal + data */}
      <div className="mt-3 pt-3 flex items-center justify-between gap-2 text-xs" style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}>
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <Icon name="send" size={14} className="flex-none" />
          <span className="truncate">{channelName ?? campaign.channelId}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 flex-none">
          <Icon name="calendar" size={14} />
          {campaign.scheduledFor ? formatDate(campaign.scheduledFor) : formatDate(campaign.createdAt)}
        </span>
      </div>
    </div>
  );
};

CampaignCard.displayName = 'CampaignCard';
